import React, {useEffect, useState} from 'react'
import {Box, TextField, Typography} from '@mui/material'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import SelectNew from 'src/components-new/inputs/SelectNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import RadioGroupNew from 'src/components-new/inputs/RadioGroupNew'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'
import {useAppSelector} from 'src/hooks'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

/**
 * New-style port of src/components/templates/CalendarDialog/CalendarDialog.tsx, scoped
 * to its single usage inside the patient-detail Activities tab, where
 * `patientDetailUser` (the patient whose detail page it's on) is always known.
 *
 * Compared to the legacy generic dialog:
 * - The patient-lookup magnify/close buttons and PatientLookupDialog/DoctorsLookupDialog
 *   are dropped entirely — the "Paziente" field is always a plain read-only display of
 *   `patientDetailUser`'s name.
 * - The two dead, never-rendered `questionsForDoctor`/`patientNotes` fields (legacy
 *   commented-out block marked "NON CANCELLARE") are dropped.
 * - The leftover `console.log('profileConfig', profileConfig)` debug statement is dropped.
 */

/** The four valid reminder-unit values (milliseconds), matching legacy warningOptions. */
type WarningUnit = 86400000 | 604800000 | 2592000000 | 31536000000

const WARNING_UNIT_OPTIONS = [
  {label: 'Giorni', value: '86400000'},
  {label: 'Settimane', value: '604800000'},
  {label: 'Mesi', value: '2592000000'},
  {label: 'Anni', value: '31536000000'},
]

const SITE_OPTIONS = [
  {label: 'Centro clinico', value: 'CLINICAL_CENTER'},
  {label: 'Laboratorio analisi', value: 'ANALYSIS_LABORATORY'},
  {label: 'Ambulatorio MMG/PLS', value: 'MMG_PLS_CLINIC'},
]

const CHANNEL_OPTIONS = [
  {label: 'Notifica tramite sms', value: 'sms'},
  {label: 'Notifica tramite e-mail', value: 'e-mail'},
  {label: 'Notifica tramite push', value: 'push'},
]

const SHARE_OPTIONS = [
  {label: 'Sì', value: 'true'},
  {label: 'No', value: 'false'},
]

/**
 * Converts a total milliseconds reminder value back to a {value, unit} pair used to
 * pre-populate the "Avvisami" fields. Mirrors legacy `computeWarning`.
 */
function computeWarning(reminderMilliseconds: number | undefined): {
  value: number | undefined
  unit: WarningUnit | undefined
} {
  if (reminderMilliseconds === undefined) {
    return {value: undefined, unit: undefined}
  }
  if (reminderMilliseconds < 604800000) {
    return {value: reminderMilliseconds / 86400000, unit: 86400000}
  }
  if (reminderMilliseconds < 2592000000) {
    return {value: reminderMilliseconds / 604800000, unit: 604800000}
  }
  if (reminderMilliseconds < 31536000000) {
    return {value: reminderMilliseconds / 2592000000, unit: 2592000000}
  }
  return {value: reminderMilliseconds / 31536000000, unit: 31536000000}
}

/**
 * Merges a calendar day (year/month/date taken from `day`) with a clock time
 * (hour/minute taken from `time`) into a single ISO-8601 string. Mirrors the legacy
 * pattern of `values.startDate.set({year, month, date}).toISOString()`.
 */
function mergeDateAndTime(day: string | null, time: string | null): string | null {
  if (day === null || time === null) return null
  const dayjsDay = dayjs(day)
  const dayjsTime = dayjs(time)
  if (!dayjsDay.isValid() || !dayjsTime.isValid()) return null
  return dayjsTime
    .set('year', dayjsDay.year())
    .set('month', dayjsDay.month())
    .set('date', dayjsDay.date())
    .toISOString()
}

interface PatientEventDialogNewProps {
  open: boolean
  event?: Record<string, any>
  eventsTypes: Record<string, any>[]
  patientDetailUser?: Record<string, any>
  onClose: () => void
  onSave: (values: Record<string, any>) => void
}

export default function PatientEventDialogNew({
  open,
  event,
  eventsTypes,
  patientDetailUser,
  onClose,
  onSave,
}: PatientEventDialogNewProps): JSX.Element {
  const {participantUserGroup} = useAppSelector(state => state.pspConfigReducer.data.agendaConfigurations)
  const {userGroup} = useAppSelector(state => state.userReducer.data)

  const [eventTypeId, setEventTypeId] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [site, setSite] = useState<string>('')
  const [startDay, setStartDay] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<string | null>(null)
  const [endTime, setEndTime] = useState<string | null>(null)
  const [warningValue, setWarningValue] = useState<string>('')
  const [warningUnit, setWarningUnit] = useState<string>('')
  const [channels, setChannels] = useState<string[]>([])
  const [shareWithPatient, setShareWithPatient] = useState<string>('')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (!open) return

    const foundType = eventsTypes?.find(eventType => eventType.name === event?.title)
    setEventTypeId(foundType?._id ?? '')
    setTitle(event?.title ?? '')
    setSite(event?.site ?? '')
    // `event` here comes from the patient user-events list (getPatientsUserEvents), which
    // aggregates heterogeneous activity types under a unified `plannedDate` start field
    // (same field the "Data inizio" table column reads) — it doesn't carry `startDate`.
    // `startDate`/`endDate` is what postAppointment/personal-events return/accept, so try
    // that first (kept for compatibility) and fall back to `plannedDate`. For a brand-new
    // event (no `event` at all) mirror legacy CalendarDialog's `moment(event?.startDate)`,
    // which collapses to "now" when the source date is undefined.
    const eventStart = event?.startDate ?? event?.plannedDate ?? (event ? null : dayjs().toISOString())
    setStartDay(eventStart)
    setStartTime(eventStart)
    setEndTime(event?.endDate ?? eventStart)
    setChannels(event?.channels ?? [])
    setShareWithPatient(event?.shareWithPatient === undefined ? '' : String(event.shareWithPatient))

    const {value: wValue, unit: wUnit} = computeWarning(event?.reminderMilliseconds)
    setWarningValue(wValue !== undefined ? String(wValue) : '')
    setWarningUnit(wUnit !== undefined ? String(wUnit) : '')

    setSubmitted(false)
  }, [open, event, eventsTypes])

  // Guard is about the VIEWER's role (logged-in user), not the patient being viewed.
  const isPatientOrInsurance = Boolean(
    userGroup?.toUpperCase().includes('PATIENT') || userGroup?.toUpperCase().includes('INSURANCEUSER')
  )
  const isReadOnly = Boolean(userGroup?.toUpperCase().includes('PATIENT') && event?.shareWithPatient)

  const reasonOptions = (eventsTypes ?? []).map(eventType => ({
    label: eventType.name as string,
    value: eventType._id as string,
  }))

  const patientName = patientDetailUser?.firstName && patientDetailUser?.lastName
    ? `${patientDetailUser.firstName} ${patientDetailUser.lastName}`
    : undefined

  const endTimeInvalid = Boolean(startTime && endTime && !dayjs(endTime).isAfter(dayjs(startTime)))

  const handleSubmit = () => {
    setSubmitted(true)
    if (!eventTypeId || !startDay || !startTime || !endTime || endTimeInvalid) return

    const startISO = mergeDateAndTime(startDay, startTime)
    const endISO = mergeDateAndTime(startDay, endTime)
    if (!startISO || !endISO) return

    const warningValueNum = warningValue !== '' ? Number(warningValue) : undefined
    const warningUnitNum = warningUnit !== '' ? (Number(warningUnit) as WarningUnit) : undefined

    // Mirrors legacy onFinish: merge date+time, drop the transient startDay field,
    // stamp type/participantUserGroup, and use patientDetailUser as the event's user
    // (currentUser is just an alias for patientDetailUser here since lookup is removed).
    const body: Record<string, any> = {
      eventTypeId,
      title,
      site: site || undefined,
      startDate: startISO,
      endDate: endISO,
      channels: channels ?? [],
      shareWithPatient: shareWithPatient === '' ? undefined : shareWithPatient === 'true',
      reminderMilliseconds:
        warningValueNum !== undefined && warningUnitNum !== undefined
          ? warningValueNum * warningUnitNum
          : undefined,
      user: patientDetailUser,
      type: 'PERSONAL_EVENT',
      participantUserGroup,
    }

    onSave(body)
  }

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={event ? (event.title || 'Evento paziente') : 'Nuovo evento paziente'}
      maxWidth="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>Annulla</Button>
          <Button variant="primary" onClick={handleSubmit}>Salva</Button>
        </>
      }
    >
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2, pt: 1}}>
        <SelectNew
          sx={{gridColumn: '1 / -1'}}
          label="Motivo"
          required
          value={eventTypeId}
          onChange={(value) => {
            setEventTypeId(value)
            setTitle(eventsTypes?.find(eventType => eventType._id === value)?.name ?? '')
          }}
          options={reasonOptions}
          disabled={isReadOnly}
          error={submitted && !eventTypeId}
          helperText={submitted && !eventTypeId ? 'Campo obbligatorio' : undefined}
        />

        <SelectNew
          label="Dove"
          value={site}
          onChange={setSite}
          options={SITE_OPTIONS}
          disabled={isReadOnly}
        />

        <DatePickerNew
          label="Data evento"
          required
          value={startDay}
          onChange={setStartDay}
          disabled={isReadOnly}
          error={submitted && !startDay}
          helperText={submitted && !startDay ? 'Campo obbligatorio' : undefined}
        />

        <TimePickerNew
          label="Dalle"
          required
          value={startTime}
          onChange={setStartTime}
          disabled={isReadOnly}
          error={submitted && !startTime}
          helperText={submitted && !startTime ? 'Campo obbligatorio' : undefined}
        />

        <TimePickerNew
          label="Alle"
          required
          value={endTime}
          onChange={setEndTime}
          disabled={isReadOnly}
          error={(submitted && !endTime) || (submitted && endTimeInvalid)}
          helperText={
            submitted && !endTime
              ? 'Campo obbligatorio'
              : submitted && endTimeInvalid
                ? 'L\'orario di inizio deve essere inferiore all\'orario di fine'
                : undefined
          }
        />

        <Box>
          <Typography
            variant="caption"
            sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
          >
            Avvisami
          </Typography>
          <Box sx={{display: 'flex', gap: 1, mt: 0.5}}>
            <TextField
              type="number"
              size="small"
              value={warningValue}
              onChange={(e) => setWarningValue(e.target.value)}
              sx={{flex: 1, '& .MuiOutlinedInput-root': {height: CONTROL_HEIGHT}}}
            />
            <SelectNew
              value={warningUnit}
              onChange={setWarningUnit}
              options={WARNING_UNIT_OPTIONS}
              placeholder="Seleziona unità di tempo"
              sx={{flex: 1}}
            />
          </Box>
        </Box>

        <CheckboxGroupNew
          sx={{gridColumn: '1 / -1'}}
          label="Metodo di avviso"
          value={channels}
          onChange={setChannels}
          options={CHANNEL_OPTIONS}
        />

        {!isPatientOrInsurance && (
          <>
            <Box sx={{gridColumn: '1 / -1'}}>
              <ReadOnlyField label="Paziente" value={patientName} fullWidth />
            </Box>

            {participantUserGroup === 'PATIENT' && (
              <RadioGroupNew
                sx={{gridColumn: '1 / -1'}}
                label="Condividi con il paziente"
                value={shareWithPatient}
                onChange={setShareWithPatient}
                options={SHARE_OPTIONS}
              />
            )}
          </>
        )}
      </Box>
    </AppDialog>
  )
}
